import { Spacer, Text } from "@hackthenorth/north";
import { Button } from "north.js";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { PageWrapper } from "src/shared/components";
import { errorToast } from "src/shared/components/Toast";
import { formatValidationIssues } from "src/shared/utils/validationMessages";
import { ArrowRight, InfoIcon } from "src/static/icons";
import { hackerCardVisual } from "src/static/img";

import { getPublishValidationIssues } from "./constants";
import ContactFields from "./sections/ContactFields";
import CoreFields from "./sections/CoreFields";
import TagsFields from "./sections/TagsFields";
import TeamFields from "./sections/TeamFields";
import {
  actionButtonTextStyles,
  fieldLabelStyles,
  subtitleTextStyles,
} from "./styles";
import { ProfileFormProps, ProfileFormValues, TargetStage } from "./types";

const ProfileForm: React.FC<ProfileFormProps> = ({
  values,
  onSubmit,
  onValuesChange,
  onSkip,
  isSubmitting,
  mode,
}) => {
  const [staging, setStaging] = useState<ProfileFormValues>(values);
  const stagingRef = useRef(staging);
  const [wantsProfile, setWantsProfile] = useState(true);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    stagingRef.current = values;
    setStaging(values);
  }, [values]);

  const setStagingField = <K extends keyof ProfileFormValues>(
    field: K,
    value: ProfileFormValues[K]
  ) => {
    const next = { ...stagingRef.current, [field]: value };
    stagingRef.current = next;
    setStaging(next);
    if (onValuesChange) onValuesChange(next);
  };

  const handleSubmit = async (targetStage: TargetStage) => {
    // Drafts can be saved at any point; only publishing requires the
    // profile to be complete (mirrors backend validation).
    const validationIssues = getPublishValidationIssues(staging);
    const canPublish = validationIssues.every(({ isValid }) => isValid);
    if (targetStage === "published" && !canPublish) {
      setShowErrors(true);
      window.scrollTo(0, 0);
      errorToast(formatValidationIssues(validationIssues));
      return;
    }

    await onSubmit(staging, targetStage);
  };

  const content = (
    <>
      <OverviewLayout>
        <OverviewContent>
          <Title mods="heading h2">Hacker Directory</Title>

          <BodyText>
            The legendary #looking-for-team Slack channel is officially
            retiring. To replace it, this year, we&rsquo;re launching the{" "}
            <strong>Hacker Directory</strong> so you can find the perfect
            teammates, or just make some cool friends, before the event even
            starts! It works in two steps: create your profile, and then browse
            the directory.
          </BodyText>

          <BodyText>
            <strong>Create a profile:</strong> Fill out your directory profile
            below to auto-launch your profile the moment the directory opens!
            It&rsquo;s the best way to get yourself, your skills, and your ideas
            out there early. Your info is only accessible to other accepted
            hackers.
          </BodyText>

          <BodyText>
            You can also skip making a profile for now. Others won&rsquo;t be
            able to find you, but you can still browse the directory when it
            opens.
          </BodyText>

          <InfoBanner>
            <StyledInfoIcon width={16} height={16} />
            <InfoText>
              You can change your mind, edit your info, or toggle your
              visibility inside the hacker directory anytime.
            </InfoText>
          </InfoBanner>

          <RequirementText>
            To publish, all questions are required unless marked Optional. You
            can save an incomplete profile as a draft.
          </RequirementText>

          <Section>
            <FieldLabel>Do you want to create a profile?</FieldLabel>
            <ToggleRow>
              <ToggleLabel isActive={!wantsProfile}>No</ToggleLabel>
              <ToggleTrack
                isOn={wantsProfile}
                onClick={() => setWantsProfile((prev) => !prev)}
                type="button"
                aria-label="Toggle create profile"
              >
                <ToggleKnob isOn={wantsProfile} />
              </ToggleTrack>
              <ToggleLabel isActive={wantsProfile}>Yes</ToggleLabel>
            </ToggleRow>
            {!wantsProfile && (
              <>
                <Spacer height={12} />
                <SkipMessage>
                  Sounds good, you can always create a profile later!
                </SkipMessage>
              </>
            )}
          </Section>
        </OverviewContent>

        <HackerCardImage src={hackerCardVisual} alt="Hacker card preview" />
      </OverviewLayout>

      {wantsProfile ? (
        <FormSections>
          <Section>
            <CoreFields
              staging={staging}
              setStagingField={setStagingField}
              showErrors={showErrors}
            />
          </Section>
          <Section>
            <TagsFields
              staging={staging}
              setStagingField={setStagingField}
              showErrors={showErrors}
            />
          </Section>
          <SectionDivider />
          <Section>
            <TeamFields
              staging={staging}
              setStagingField={setStagingField}
              showErrors={showErrors}
              mode={mode}
            />
          </Section>
          <SectionDivider />
          <Section>
            <ContactFields
              staging={staging}
              setStagingField={setStagingField}
              showErrors={showErrors}
            />
          </Section>

          <SectionDivider />
          <StickyActions>
            <ActionsRow>
              <PublishButton
                color="primary"
                disabled={isSubmitting}
                onClick={() => handleSubmit("published")}
              >
                <ButtonContent>
                  Publish and continue
                  <ArrowRight width={16} height={16} />
                </ButtonContent>
              </PublishButton>
              <DraftButton
                color="secondary"
                disabled={isSubmitting}
                onClick={() => handleSubmit("draft")}
              >
                Save as draft and continue
              </DraftButton>
            </ActionsRow>
          </StickyActions>
        </FormSections>
      ) : (
        onSkip && (
          <ActionsRow>
            <PublishButton
              color="primary"
              disabled={isSubmitting}
              onClick={() => onSkip()}
            >
              <ButtonContent>
                Skip and continue
                <ArrowRight width={16} height={16} />
              </ButtonContent>
            </PublishButton>
          </ActionsRow>
        )
      )}
    </>
  );

  return mode === "rsvp" ? content : <PageWrapper>{content}</PageWrapper>;
};

export default ProfileForm;

const Section = styled.section`
  margin-bottom: 32px;
`;

const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  margin: 8px 0 32px;
`;

const OverviewLayout = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;

  ${({ theme }) => theme.mediaQueries.tablet`
    flex-direction: column;
  `}
`;

const OverviewContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const FormSections = styled.div`
  width: 100%;

  ${({ theme }) => theme.mediaQueries.medium`
    padding-bottom: calc(84px + env(safe-area-inset-bottom, 0px));
  `}

  ${({ theme }) => theme.mediaQueries.largeMobile`
    padding-bottom: calc(144px + env(safe-area-inset-bottom, 0px));
  `}
`;

const Title = styled(Text)`
  margin-bottom: 16px;
`;

const BodyText = styled.p`
  ${subtitleTextStyles}
  color: ${({ theme }) => theme.globalConstants.color.brandSecondary};
  margin: 0 0 16px;
`;

const RequirementText = styled.p`
  ${subtitleTextStyles}
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  margin: 0 0 16px;
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 24px;
  background: ${({ theme }) =>
    theme.globalConstants.color.backgroundPrimaryLight};
  border-radius: 8px;
`;

const StyledInfoIcon = styled(InfoIcon)`
  flex-shrink: 0;
`;

const InfoText = styled.p`
  ${subtitleTextStyles}
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  margin: 0;
`;

const FieldLabel = styled.p`
  ${fieldLabelStyles}
  color: ${({ theme }) => theme.globalConstants.color.brandSecondary};
  margin: 0;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
`;

const ToggleLabel = styled.span<{ isActive: boolean }>`
  ${subtitleTextStyles}
  color: ${({ theme, isActive }) =>
    isActive
      ? theme.globalConstants.color.brandSecondary
      : theme.globalConstants.color.textTertiary};
`;

const ToggleTrack = styled.button<{ isOn: boolean }>`
  position: relative;
  width: 52px;
  height: 28px;
  border-radius: 14px;
  border: none;
  cursor: pointer;
  padding: 0;
  background: ${({ theme, isOn }) =>
    isOn
      ? theme.globalConstants.color.borderPrimary
      : theme.globalConstants.color.borderGray};
  transition: background-color 0.2s ease;
`;

const ToggleKnob = styled.div<{ isOn: boolean }>`
  position: absolute;
  top: 3px;
  left: ${({ isOn }) => (isOn ? "27px" : "3px")};
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: left 0.2s ease;
`;

const SkipMessage = styled.p`
  ${subtitleTextStyles}
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  margin: 0;
`;

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;

  ${({ theme }) => theme.mediaQueries.medium`
    width: 100%;
    max-width: 700px;
    flex-wrap: nowrap;
    margin: 0 auto;
  `}

  ${({ theme }) => theme.mediaQueries.largeMobile`
    flex-direction: column;
  `}
`;

const StickyActions = styled.div`
  ${({ theme }) => theme.mediaQueries.medium`
    position: fixed;
    z-index: 20;
    right: 0;
    bottom: 0;
    left: 0;
    box-sizing: border-box;
    width: 100%;
    padding: 12px max(24px, env(safe-area-inset-right, 0px))
      calc(12px + env(safe-area-inset-bottom, 0px))
      max(24px, env(safe-area-inset-left, 0px));
    border-top: 1px solid ${theme.globalConstants.color.borderGray};
    background: ${theme.globalConstants.color.white};
    box-shadow: 0 -4px 12px rgb(0 0 0 / 8%);
  `}
`;

const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const PublishButton = styled(Button)`
  ${actionButtonTextStyles}
  border-radius: 8px !important;
  padding: 12px 20px !important;
  white-space: nowrap;

  ${({ theme }) => theme.mediaQueries.medium`
    width: 100%;
    flex: 1 1 0;
  `}
`;

const DraftButton = styled(Button)`
  ${actionButtonTextStyles}
  border-radius: 8px !important;
  padding: 12px 20px !important;
  white-space: nowrap;
  color: ${({ theme }) =>
    theme.globalConstants.color.brandSecondary} !important;
  border-color: ${({ theme }) =>
    theme.globalConstants.color.borderGray} !important;
  border-width: 1px !important;
  border-style: solid !important;
  background: ${({ theme }) => theme.globalConstants.color.white} !important;

  &:hover,
  &:focus {
    background: ${({ theme }) =>
      theme.globalConstants.color.backgroundLight} !important;
  }

  ${({ theme }) => theme.mediaQueries.medium`
    width: 100%;
    flex: 1 1 0;
  `}
`;

const HackerCardImage = styled.img`
  width: 280px;
  flex-shrink: 0;
  border-radius: 12px;

  ${({ theme }) => theme.mediaQueries.tablet`
    align-self: center;
  `}
`;
